import { Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthPageLayoutComponent } from '../../../layout/auth-page-layout/auth-page-layout.component';
import { AdminPreviewContentType, AdminPreviewService } from '../../../shared/services/admin-preview.service';

const ROUTE_SEGMENT: Record<AdminPreviewContentType, string> = {
  POST: 'post',
  BLOG: 'blog',
  EVENT: 'event',
};

/**
 * Landing page for the admin panel's "open on site" links.
 *
 * The admin panel sends the browser here with a short-lived preview token
 * (?token=…) for ONE post, blog or event. We check it with the API, keep it
 * for this tab only, and open that item — no login needed, and whoever is
 * (or isn't) signed in on this browser is left untouched.
 */
@Component({
  selector: 'app-admin-preview',
  standalone: true,
  imports: [CommonModule, RouterModule, AuthPageLayoutComponent],
  templateUrl: './admin-preview.html',
})
export class AdminPreview implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private preview = inject(AdminPreviewService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  status = signal<'loading' | 'error'>('loading');
  message = signal('');

  ngOnInit(): void {
    if (!this.isBrowser) return;

    const token = this.route.snapshot.queryParamMap.get('token');

    // Get the token out of the address bar / history straight away.
    this.router.navigate([], { queryParams: {}, replaceUrl: true });

    if (!token) {
      this.fail('This preview link is invalid.');
      return;
    }

    this.preview.verify(token).subscribe({
      next: (res: any) => {
        const data = res?.data ?? {};
        const contentType = data.contentType as AdminPreviewContentType;
        const contentId = data.contentId ? String(data.contentId) : '';
        const segment = ROUTE_SEGMENT[contentType];
        const expiresAt = data.expiresAt ? new Date(data.expiresAt).getTime() : 0;

        if (!segment || !contentId || !expiresAt) {
          this.fail('This preview link is invalid.');
          return;
        }

        const path = `/${segment}/${contentId}`;
        this.preview.saveGrant({ token, contentType, contentId, path, expiresAt });
        this.router.navigateByUrl(path, { replaceUrl: true });
      },
      error: (err: any) => {
        this.fail(err?.error?.message || 'This preview link is invalid or has expired.');
      },
    });
  }

  private fail(message: string): void {
    this.message.set(message);
    this.status.set('error');
  }
}
